import classnames from 'classnames';
import type {ReactNode} from 'react';
import React, {useCallback, useState} from 'react';
import {ConfirmationModal} from '@albert-io/atomic';

import AnnotationPopover from './AnnotationPopover';
import {useAnnotationModeStore} from './AnnotationModeStore';
import {
  selectAnnotationNote,
  selectAnnotationType,
  selectHighlightColor
} from './annotationSelectors';
import type {AnnotationAnchor} from './annotationTypes';
import {useAnnotations} from './useAnnotations';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';

import './annotations-cursor.scss';
import './annotations-highlights.scss';

interface AnnotatableContentProps {
  regionId: string | null;
  /** Language of the rendered text ('es' when translated); null = original. */
  lang?: string | null;
  /**
   * Styles the region wrapper div (e.g. `contents` where it must stay
   * layout-transparent). Unavailable/flag-off renders bare children without it.
   */
  className?: string;
  children: ReactNode;
}

/**
 * Wraps children so selections inside them become annotations (see
 * `useAnnotations`). Anchors index the region's normalized visible text
 * (annotationTextProjection.ts), not the source markdown.
 */
export default function AnnotatableContent({
  regionId,
  lang = null,
  className,
  children
}: AnnotatableContentProps) {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();

  if (!isAnnotationModeAvailable || !regionId) {
    return <>{children}</>;
  }

  return (
    <AnnotatableRegion regionId={regionId} lang={lang} className={className}>
      {children}
    </AnnotatableRegion>
  );
}

function AnnotatableRegion({
  regionId,
  lang,
  className,
  children
}: {
  regionId: string;
  lang: string | null;
  className?: string;
  children: ReactNode;
}) {
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const {
    captureAnnotation,
    getAnnotationAtPoint,
    containerRef,
    isMergeConfirmPending,
    confirmPendingMerge,
    cancelPendingMerge
  } = useAnnotations(regionId, lang);
  const setHighlightColor = useAnnotationModeStore((state) => state.setHighlightColor);
  const setAnnotationUnderline = useAnnotationModeStore((state) => state.setAnnotationUnderline);
  const setAnnotationNote = useAnnotationModeStore((state) => state.setAnnotationNote);
  const removeAnnotation = useAnnotationModeStore((state) => state.removeAnnotation);
  const [activeAnnotation, setActiveAnnotation] = useState<AnnotationAnchor | null>(null);
  // The active annotation's own color, type, and note drive the popover.
  const activeColor = useAnnotationModeStore((state) =>
    selectHighlightColor(state, regionId, activeAnnotation?.id)
  );
  const activeType = useAnnotationModeStore((state) =>
    selectAnnotationType(state, regionId, activeAnnotation?.id)
  );
  const activeNote = useAnnotationModeStore((state) =>
    selectAnnotationNote(state, regionId, activeAnnotation?.id)
  );
  const activeAnnotationId = activeAnnotation?.id;
  const handleNoteChange = useCallback(
    (note: string) => {
      if (activeAnnotationId) {
        setAnnotationNote(regionId, activeAnnotationId, note);
      }
    },
    [regionId, activeAnnotationId, setAnnotationNote]
  );

  const handleMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isAnnotationModeOn) {
        return;
      }
      const captured = captureAnnotation();
      if (captured) {
        setActiveAnnotation(captured);
        return;
      }
      // Don't hijack a real text selection (e.g. select-to-copy).
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        return;
      }
      // A plain click on an existing annotation opens its style editor.
      setActiveAnnotation(getAnnotationAtPoint(event.clientX, event.clientY));
    },
    [isAnnotationModeOn, captureAnnotation, getAnnotationAtPoint]
  );

  return (
    <>
      <div
        ref={containerRef}
        className={isAnnotationModeOn ? classnames(className, 'cursor-highlighter') : className}
        onMouseUp={handleMouseUp}
      >
        {children}
      </div>
      {isMergeConfirmPending && (
        // Placeholder presentation: design wants this anchored to the selection
        // like the style popover, with final copy TBD (#19010 Slack thread).
        <ConfirmationModal
          body='Do you want to merge these annotations?'
          confirmText='Merge'
          cancelText='Cancel'
          onConfirm={() => setActiveAnnotation(confirmPendingMerge())}
          onCancel={() => {
            setActiveAnnotation(null);
            cancelPendingMerge();
          }}
        />
      )}
      {!isMergeConfirmPending &&
        activeAnnotation &&
        activeColor &&
        activeType && (
          // Keyed so switching marks always remounts with that mark's own note draft.
          <AnnotationPopover
            key={activeAnnotation.id}
            anchorRect={activeAnnotation.rect}
            selectedColor={activeColor}
            selectedType={activeType}
            note={activeNote ?? ''}
            onSelectColor={(color) => setHighlightColor(regionId, activeAnnotation.id, color)}
            onSelectUnderline={() => setAnnotationUnderline(regionId, activeAnnotation.id)}
            onNoteChange={handleNoteChange}
            onRemove={() => {
              removeAnnotation(regionId, activeAnnotation.id);
              setActiveAnnotation(null);
            }}
            onDismiss={() => setActiveAnnotation(null)}
          />
        )}
    </>
  );
}
